import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Mail, Terminal, Globe } from 'lucide-react';

export const Footer = () => {
  const { t } = useTranslation();
  const [showPrivacy, setShowPrivacy] = useState(false);

  const sections = {
    contact: {
      title: t('footer.contact.title'),
      items: [
        { label: t('footer.contact.email'), href: 'mailto:victor@victorlazari.com', icon: Mail },
        { label: t('footer.contact.hq'), href: '#', icon: Globe },
      ],
    },
    quickLinks: {
      title: t('footer.quickLinks.title'),
      items: [
        { label: t('footer.quickLinks.home'), href: '/' },
        { label: t('footer.quickLinks.solutions'), href: '/solutions' },
        { label: 'Vector OS', href: '/vector' },
        { label: t('footer.quickLinks.contact'), href: '/contact' },
      ],
    },
    legal: {
      title: t('footer.legal.title'),
      items: [
        { label: t('footer.legal.privacy'), href: '/privacy' },
        { label: t('footer.legal.terms'), href: '/terms' },
        { label: t('footer.legal.cookies'), href: '/cookies' },
        { label: t('footer.legal.preferences'), onClick: (e: React.MouseEvent<HTMLButtonElement>) => { e.preventDefault(); (window as any).showCookieConsent?.(); } },
      ],
    },
  };

  return (
    <footer className="bg-slate-950 border-t border-slate-900 text-slate-400 font-sans selection:bg-cyan-500/30 relative overflow-hidden">
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      <div className="max-w-7xl mx-auto pt-16 pb-8 px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          
          {/* Brand Column */}
          <div className="col-span-1 md:col-span-1">
             <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-center text-cyan-500">
                   <Terminal size={20} />
                </div>
                <div>
                   <h3 className="font-bold text-white text-lg leading-none">Victor Lazari</h3>
                   <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{t('footer.brand.role')}</span>
                </div>
             </div>
             <p className="text-sm leading-relaxed text-slate-500 mb-6">
               {t('footer.brand.description')}
             </p>
          </div>

          {/* Links Columns */}
          {Object.values(sections).map((section, idx) => (
            <div key={idx}>
              <h3 className="text-xs font-bold text-white tracking-widest uppercase mb-6 flex items-center gap-2">
                 <div className="w-1 h-3 bg-cyan-500 rounded-full" />
                 {section.title}
              </h3>
              <ul className="space-y-3">
                {section.items.map((item: any, itemIdx: number) => (
                  <li key={itemIdx}>
                    {item.href ? (
                      <a href={item.href} className="text-sm hover:text-cyan-400 transition-colors flex items-center gap-2 group">
                        {item.icon && <item.icon size={14} className="text-slate-600 group-hover:text-cyan-500 transition-colors" />}
                        {item.label}
                      </a>
                    ) : item.onClick ? (
                      <button onClick={item.onClick} className="text-sm hover:text-cyan-400 text-left transition-colors">
                        {item.label}
                      </button>
                    ) : (
                      <span className="text-sm">{item.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* System Status Bar */}
        <div className="border-t border-slate-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-mono text-slate-600">
          <p>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>

      {/* Privacy Policy Modal - Kept functional but styled */}
      <AnimatePresence>
        {showPrivacy && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-50"
              onClick={() => setShowPrivacy(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              className="fixed inset-4 md:inset-20 bg-slate-900 border border-slate-800 rounded-2xl z-50 overflow-hidden flex flex-col shadow-2xl"
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-800 bg-slate-900">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <Shield className="w-5 h-5 text-cyan-500" />
                  {t('privacy.title')}
                </h2>
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="text-slate-500 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 p-8 text-slate-300">
                 {/* Content remains same, just styled containers if needed */}
                 <div className="prose prose-invert max-w-none">
                    <p>Legal documentation loaded securely.</p>
                    {/* Mapping logic would go here as before */}
                 </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </footer>
  );
};
